import React, { useEffect, useMemo, useState } from 'react';
import configService, { ConfigEntry } from '../services/configService';
import { PageHeader, StatusBadge, LoadingState, useConfirm } from '../components/ui';
import type { StatusTone } from '../components/ui';
import { routeById } from '../navigation/routes';

// Herkunft eines Werts: Mandant ueberschreibt Global ueberschreibt .env.
const SOURCE_LABEL: Record<string, { label: string; tone: StatusTone }> = {
  tenant: { label: 'Mandant', tone: 'success' },
  global: { label: 'Global', tone: 'info' },
  env: { label: '.env', tone: 'neutral' },
  unset: { label: 'nicht gesetzt', tone: 'neutral' },
};

const OptionsPage: React.FC = () => {
  const { confirm } = useConfirm();
  const [entries, setEntries] = useState<ConfigEntry[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [savingKey, setSavingKey] = useState('');

  const load = async () => {
    try {
      const data = await configService.getConfig();
      setEntries(data);
      // Nicht-Secret-Werte vorbefüllen; Secrets bleiben leer (Platzhalter).
      const d: Record<string, string> = {};
      data.forEach((e) => {
        if (e.type !== 'secret') d[e.key] = e.value ?? '';
      });
      setDrafts(d);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Konfiguration konnte nicht geladen werden (Owner/Admin erforderlich)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, ConfigEntry[]>();
    entries.forEach((e) => {
      if (!map.has(e.group)) map.set(e.group, []);
      map.get(e.group)!.push(e);
    });
    return Array.from(map.entries());
  }, [entries]);

  const flash = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const save = async (entry: ConfigEntry) => {
    setError('');
    setSavingKey(entry.key);
    try {
      const value = drafts[entry.key] ?? '';
      if (entry.type === 'secret' && value === '') {
        setError('Bitte einen Wert eingeben (leer lassen behält den bestehenden Wert).');
        return;
      }
      await configService.setConfig(entry.key, value);
      flash(`${entry.label} gespeichert.`);
      setDrafts((d) => ({ ...d, [entry.key]: entry.type === 'secret' ? '' : value }));
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSavingKey('');
    }
  };

  const reset = async (entry: ConfigEntry) => {
    const ok = await confirm({
      title: `„${entry.label}“ zurücksetzen?`,
      message: 'Der mandanteneigene Wert wird gelöscht; danach gilt wieder der globale bzw. .env-Standard. Rückgängig nur durch erneutes Eingeben und Speichern.',
      confirmLabel: 'Zurücksetzen',
      tone: 'danger',
    });
    if (!ok) return;
    setError('');
    try {
      await configService.resetConfig(entry.key);
      flash(`${entry.label} zurückgesetzt.`);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Zurücksetzen fehlgeschlagen');
    }
  };

  const header = <PageHeader title="Optionen" subtitle={routeById('options')?.description} hilfe="optionen" />;

  if (loading) {
    return (
      <div className="page-container">
        {header}
        <LoadingState label="Lade Optionen…" />
      </div>
    );
  }

  return (
    <div className="page-container">
      {header}
      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        Hier setzen Sie die Integrations-Zugangsdaten <strong>pro Mandant</strong>. Werte haben Vorrang
        vor den <code>.env</code>-Standards; Secrets werden verschlüsselt gespeichert und nie im Klartext
        angezeigt. Leeres Speichern bei einem Secret behält den bestehenden Wert; „Zurücksetzen“ fällt
        auf den globalen/<code>.env</code>-Standard zurück.
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {groups.map(([group, items]) => (
        <div className="card" key={group} style={{ marginBottom: '1.5rem' }}>
          <h3 className="card-title">{group}</h3>
          {items.map((e) => {
            const source = SOURCE_LABEL[e.source] || SOURCE_LABEL.unset;
            return (
              <div className="form-group" key={e.key} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  {e.label}
                  <StatusBadge tone={source.tone}>{source.label}</StatusBadge>
                  {e.type === 'secret' && e.isSet && <StatusBadge tone="success">gesetzt</StatusBadge>}
                </label>
                {e.description && (
                  <p className="text-xs text-muted" style={{ margin: '0.15rem 0' }}>
                    {e.description}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    className="form-input"
                    type={e.type === 'secret' ? 'password' : 'text'}
                    autoComplete="new-password"
                    aria-label={e.label}
                    placeholder={e.type === 'secret' ? (e.isSet ? '•••••••• (gesetzt)' : 'nicht gesetzt') : ''}
                    value={drafts[e.key] ?? ''}
                    onChange={(ev) => setDrafts((d) => ({ ...d, [e.key]: ev.target.value }))}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={savingKey === e.key}
                    onClick={() => save(e)}
                  >
                    {savingKey === e.key ? 'Speichert…' : 'Speichern'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={e.source !== 'tenant'}
                    title={e.source === 'tenant' ? 'Auf .env/globalen Standard zurücksetzen' : 'Kein mandanten-eigener Wert gesetzt'}
                    onClick={() => reset(e)}
                  >
                    Zurücksetzen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default OptionsPage;
